/**
 * localTaps — IndexedDB store for tile tap counts + last-tapped timestamps.
 *
 * Founder ruling (2026-06): tap_events are STRICT LOCAL-ONLY for launch.
 * Button-press history lives on-device only. This matches the privacy
 * promise: "Zero tap history on our servers."
 *
 * Storage shape:
 *   key   = `${profileId}:${tileId}`
 *   value = { tap_count: number, last_tapped: ISO string }
 *
 * The server no longer receives tap events. The `POST /tiles/{id}/tap`
 * route on the backend is now a no-op that returns 200 for back-compat
 * with any offline-queued old client — see routes/tiles.py.
 */

const DB_NAME = "sonspeak_local_taps";
const STORE_NAME = "tap_counters";
const DB_VERSION = 1;

let _dbPromise = null;

function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is not available in this environment."));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

function _key(profileId, tileId) {
  return `${profileId || ""}:${tileId || ""}`;
}

async function _read(profileId, tileId) {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const req = tx.objectStore(STORE_NAME).get(_key(profileId, tileId));
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

async function _write(profileId, tileId, value) {
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const req = tx.objectStore(STORE_NAME).put(value, _key(profileId, tileId));
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch { /* swallow */ }
}

/**
 * Increment the local tap counter for a tile. Returns the new
 * {tap_count, last_tapped} pair (also written to IDB).
 *
 * Fire-and-forget from callers — errors are swallowed because a missing
 * IDB is not worth blocking speech.
 */
export async function bumpLocalTap(profileId, tileId) {
  if (!profileId || !tileId) return null;
  const existing = (await _read(profileId, tileId)) || { tap_count: 0 };
  const next = {
    tap_count: (existing.tap_count || 0) + 1,
    last_tapped: new Date().toISOString(),
  };
  await _write(profileId, tileId, next);
  return next;
}

/**
 * Hydrate an array of tile rows with the device's tap counts. Returns
 * a new array; original objects are not mutated. Any tile without a
 * local counter gets tap_count=0 / last_tapped=null so downstream code
 * (most-used sort, favorites filter) reads consistent shapes.
 */
export async function hydrateTilesWithLocalTaps(profileId, tiles) {
  if (!Array.isArray(tiles) || tiles.length === 0) return tiles;
  const out = [];
  for (const t of tiles) {
    const local = await _read(profileId, t.id);
    out.push({
      ...t,
      tap_count: local?.tap_count ?? 0,
      last_tapped: local?.last_tapped ?? null,
    });
  }
  return out;
}

/**
 * Purge every tap counter for a profile. Used when the profile is
 * deleted or the parent triggers "Wipe device" from Settings.
 */
export async function purgeProfileTaps(profileId) {
  if (!profileId) return;
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAllKeys();
      req.onsuccess = () => {
        const keys = req.result || [];
        const prefix = `${profileId}:`;
        keys
          .filter((k) => typeof k === "string" && k.startsWith(prefix))
          .forEach((k) => store.delete(k));
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      };
      req.onerror = () => reject(req.error);
    });
  } catch { /* swallow */ }
}
